import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarX2, CalendarDays, Clock, LogOut, MapPin, Megaphone, Sparkles, UserRound } from "lucide-react";
import { api, formatApiError, useAuth } from "@/auth";
import { GROUPS, SEDES } from "@/i18n";
import Avatar from "@/components/Avatar";

const DAY_ES = { lun: "Lunes", mar: "Martes", jue: "Jueves", vie: "Viernes" };

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("es-ES", {
    timeZone: "Atlantic/Canary",
    weekday: "long",
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function StudentDashboard() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [cancellations, setCancellations] = useState([]);
  const [notices, setNotices] = useState([]);
  const [confirming, setConfirming] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/cancellations").then((r) => setCancellations(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/notices").then((r) => setNotices(r.data)).catch(() => {});
  }, []);

  const myGroup = GROUPS.find((g) => g.code === user?.group_code);
  const mySede = myGroup ? SEDES[myGroup.sede] : null;
  const visibleEvents = events.filter((e) => e.groups.includes("all") || e.groups.includes(user?.group_code));

  const confirmCancellation = async (id) => {
    setConfirming(id);
    try {
      await api.post(`/cancellations/${id}/confirm`);
      setCancellations((list) => list.map((c) => (c.id === id ? { ...c, confirmed: true } : c)));
      toast.success("Cancelación confirmada. ¡Gracias!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirming(null);
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
    navigate("/");
  };

  return (
    <div className="mx-auto max-w-7xl px-5 pb-24 pt-28 sm:px-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-5">
          <Avatar testPrefix="student" />
          <div>
            <p className="mb-2 flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.3em] text-accent">
              <UserRound size={14} /> Mi perfil
            </p>
            <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight sm:text-4xl">
              Hola, {user?.name}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-accent/15 px-4 py-1.5 font-display text-xs font-extrabold uppercase tracking-wide text-accent">
              Grupo {user?.group_code}
            </span>
            <span className="font-mono text-xs text-zinc-500">{user?.email}</span>
            </div>
          </div>
        </div>
        <button
          data-testid="student-logout"
          onClick={logout}
          className="flex items-center gap-2 rounded-full border border-white/20 px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-zinc-300 transition-all duration-300 hover:border-accent hover:text-accent"
        >
          <LogOut size={14} /> Salir
        </button>
      </div>

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-card p-6 sm:p-8">
          <div className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-accent/10 blur-[60px]" />
          <h2 className="flex items-center gap-2 font-display text-base font-extrabold uppercase tracking-tight">
            <Clock size={17} className="text-accent" /> Mi horario
          </h2>
          {myGroup ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-lg bg-accent/15 p-4">
                <p className="font-display text-2xl font-extrabold uppercase text-accent">{myGroup.code}</p>
                <p className="mt-1 text-sm font-semibold text-white">
                  {DAY_ES[myGroup.days[0]]} y {DAY_ES[myGroup.days[1]]}
                </p>
                <p className="font-mono text-sm text-zinc-400">{myGroup.time}</p>
              </div>
              <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.15em] text-zinc-400">
                <MapPin size={13} className="text-accent" /> {mySede?.name} · {mySede?.addr}
              </p>
            </div>
          ) : (
            <p className="mt-6 text-sm text-zinc-400">Sin grupo asignado.</p>
          )}
        </div>

        <div className="relative overflow-hidden rounded-xl border border-white/10 bg-card p-6 sm:p-8 lg:col-span-2">
          <h2 className="flex items-center gap-2 font-display text-base font-extrabold uppercase tracking-tight">
            <Sparkles size={17} className="text-accent" /> Eventos programados
          </h2>
          {visibleEvents.length === 0 ? (
            <p data-testid="student-events-empty" className="mt-6 rounded-lg border border-dashed border-white/15 p-6 text-center text-sm text-zinc-400">
              No hay eventos programados por ahora. ¡Muy pronto habrá novedades!
            </p>
          ) : (
            <div className="mt-6 space-y-3" data-testid="student-events-list">
              {visibleEvents.map((ev) => (
                <div key={ev.id} className="rounded-lg border border-white/10 bg-panel p-4 transition-colors duration-300 hover:border-accent/50">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="font-display text-sm font-extrabold uppercase tracking-wide">{ev.title}</p>
                    <span className="flex items-center gap-1.5 font-mono text-xs text-accent">
                      <CalendarDays size={13} /> {fmtDate(ev.date)}
                    </span>
                  </div>
                  {ev.description && <p className="mt-2 text-sm text-zinc-400">{ev.description}</p>}
                  {ev.location_url && (
                    <a
                      href={ev.location_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`event-location-${ev.id}`}
                      className="mt-2 inline-flex items-center gap-1.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-accent transition-all hover:underline"
                    >
                      <MapPin size={13} /> Ver ubicación
                    </a>
                  )}
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    {ev.groups.includes("all") ? "Todos los grupos" : ev.groups.join(" · ")}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="relative mt-6 overflow-hidden rounded-xl border border-white/10 bg-card p-6 sm:p-8">
        <h2 className="flex items-center gap-2 font-display text-base font-extrabold uppercase tracking-tight">
          <Megaphone size={17} className="text-accent" /> Mensajes de K-house
        </h2>
        {notices.length === 0 ? (
          <p data-testid="student-notices-empty" className="mt-6 rounded-lg border border-dashed border-white/15 p-6 text-center text-sm text-zinc-400">
            No hay mensajes por ahora.
          </p>
        ) : (
          <div className="mt-6 space-y-3" data-testid="student-notices-list">
            {notices.map((n) => (
              <div key={n.id} className="rounded-lg border border-white/10 bg-panel p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-display text-sm font-extrabold uppercase tracking-wide">{n.title}</p>
                  <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-zinc-500">
                    {n.created_at?.slice(0, 10)}
                  </span>
                </div>
                {n.body && <p className="mt-2 text-sm text-zinc-300">{n.body}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="relative mt-6 overflow-hidden rounded-xl border border-white/10 bg-card p-6 sm:p-8">
        <h2 className="flex items-center gap-2 font-display text-base font-extrabold uppercase tracking-tight">
          <CalendarX2 size={17} className="text-accent" /> Avisos de cancelación
        </h2>
        {cancellations.length === 0 ? (
          <p data-testid="student-cancellations-empty" className="mt-6 rounded-lg border border-dashed border-white/15 p-6 text-center text-sm text-zinc-400">
            No hay cancelaciones activas para tu grupo. Las clases van con normalidad.
          </p>
        ) : (
          <div className="mt-6 space-y-3" data-testid="student-cancellations-list">
            {cancellations.map((c) => (
              <div key={c.id} className="flex flex-wrap items-center justify-between gap-4 rounded-lg border border-accent/40 bg-accent/5 p-4">
                <div>
                  <p className="flex items-center gap-2 font-display text-sm font-extrabold uppercase tracking-wide text-white">
                    <Megaphone size={15} className="text-accent" />
                    Clase cancelada · {c.group_code === "all" ? "Todos los grupos" : `Grupo ${c.group_code}`}
                  </p>
                  <p className="mt-1 font-mono text-xs text-zinc-400">{fmtDate(c.date)}</p>
                  {c.reason && <p className="mt-2 text-sm text-zinc-300">{c.reason}</p>}
                </div>
                {c.confirmed ? (
                  <span data-testid={`cancellation-confirmed-${c.id}`} className="rounded-full border border-emerald-500/50 bg-emerald-500/10 px-4 py-2 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-emerald-400">
                    Confirmado
                  </span>
                ) : (
                  <button
                    data-testid={`cancellation-confirm-${c.id}`}
                    onClick={() => confirmCancellation(c.id)}
                    disabled={confirming === c.id}
                    className="rounded-full bg-accent px-5 py-2.5 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-accent-hover disabled:opacity-60"
                  >
                    {confirming === c.id ? "Confirmando…" : "Confirmar"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
